const ELM_NAME = 'dynamic-products';
const PRODUCT_GRID_SELECTOR = '[data-collection-dynamic-product-grid]';
const PRODUCT_TOTALS_INFO_SELECTOR = '[data-collection-product-total]';
const LOAD_MORE_BUTTONS_SELECTOR = '[data-collection-load-more]';
const PAGINATION_SELECTOR = '[data-pagination]';
const WRAPPER_SELECTOR = '#main-collection-product-grid';
const PAGINATION_METER_SELECTOR = '[data-pagination-meter]';
const PAGINATION_METER_VALUE_SELECTOR = '[data-pagination-meter-value]';
const PAGINATION_METER_TEXT_SELECTOR = '[data-pagination-meter-text]';

const isValid = (value) => value !== undefined;

class DynamicProducts extends HTMLElement {
  static get observedAttributes() {
    return ['data-page-config'];
  }

  /** @type {{ totalPages: Number, currentPage: Number, productsCount: Number, productsViewed: Number, sectionToFetch: String, firstViewedProduct: String }} */
  pageConfig = {
    sectionToFetch: ELM_NAME,
  };

  setPageConfig() {
    const pageConfig = JSON.parse(this.getAttribute('data-page-config') || '{}');
    const {
      totalPages,
      currentPage,
      productsCount,
      collectionHandle,
      productsViewed,
      sectionToFetch,
      firstViewedProduct,
    } = pageConfig;

    if (
      isValid(totalPages) &&
      isValid(currentPage) &&
      isValid(productsCount) &&
      isValid(productsViewed)
    ) {
      this.pageConfig = {
        totalPages: parseInt(totalPages),
        currentPage: parseInt(currentPage),
        productsCount: parseInt(productsCount),
        collectionHandle,
        productsViewed: parseInt(productsViewed),
        sectionToFetch,
        firstViewedProduct: parseInt(firstViewedProduct),
      };

      return this.pageConfig;
    }
  }

  setButtonDisabled(button) {
    button.disabled = true;
    button.classList.add('hidden');
    button.parentElement.classList.add('hidden');
  }

  setButtonEnabled(button) {
    button.disabled = false;
    button.classList.remove('hidden');
    button.parentElement.classList.remove('hidden');
  }

  setPaginationMeter() {
    const { productsViewed } = this.pageConfig;

    if (this.paginationMeterText && this.paginationMeterText.textContent) {
      let meterText = this.paginationMeterText.textContent;
      // Match all numbers in the text
      const match = this.paginationMeterText.textContent.match(/(\d+)/g);

      if (match && match.length === 2) {
        // Replace the first number with the number of products viewed.
        meterText = meterText.replace(match[0], productsViewed);

        if (this.paginationMeterText) this.paginationMeterText.textContent = meterText;
      }
    }

    // Set the aria values and update the width of the meter
    if (this.paginationMeter && this.paginationMeterValue) {
      this.paginationMeter.setAttribute('aria-valuenow', productsViewed);
      this.paginationMeterValue.style.width = `${
        (productsViewed / this.pageConfig.productsCount) * 100
      }%`;
    }
  }

  handleLoadMoreClick = (event) => {
    const { currentTarget } = event;
    const loadType = currentTarget.dataset.collectionLoadMore;

    this.isLoading();
    this.handleLoadMoreProducts(loadType);
  };

  handleLoadMoreProducts(loadType, previousPage) {
    const isPopState = previousPage !== undefined;
    const isNext = loadType == 'next';

    // Reset the page index if in popstate
    if (isPopState) {
      this.nextPageIndex = previousPage;
      this.previousPageIndex = previousPage;

      if (isNext) {
        this.nextPageIndex += 1;
        this.previousPageIndex = this.nextPageIndex;
      } else {
        this.previousPageIndex -= 1;
        this.nextPageIndex = this.previousPageIndex;
      }
    } else if (isNext) {
      this.nextPageIndex += 1;
    } else {
      this.previousPageIndex -= 1;
    }

    const pageToFetch = isNext ? this.nextPageIndex : this.previousPageIndex;

    const url = new URL(window.location.href);

    this.previousUrl = url;

    if (!url.searchParams.get('page')) {
      url.searchParams.append('page', `${pageToFetch}`);
    } else {
      url.searchParams.set('page', `${pageToFetch}`);
    }

    const paramsString = url.searchParams.toString();
    const fetchUrl = `${url.pathname}?section_id=${this.pageConfig.sectionToFetch}&${paramsString}`;

    const historyState = {
      page: pageToFetch,
      collectionHandle: this.pageConfig.collectionHandle,
    };

    this.renderProducts(fetchUrl, url, loadType, pageToFetch, historyState, isPopState);
  }

  async renderProducts(fetchUrl, newUrl, direction, pageToFetch, state, isPopState) {
    try {
      if (pageToFetch < 1) {
        this.isLoading(false);
        this.setButtonDisabled(this.loadMoreButtonPrevious);
        return;
      }

      if (pageToFetch > this.pageConfig.totalPages) {
        this.isLoading(false);
        this.setButtonDisabled(this.loadMoreButtonNext);
        return;
      }

      // Get the data we need
      const response = await fetch(fetchUrl);

      // Check response
      if (response.status != 200) {
        throw response;
      }

      // Get the text data
      const responseText = await response.text();

      // String to DOM
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      // Get the content from the section
      const newProducts = html.querySelector(PRODUCT_GRID_SELECTOR)?.children;

      if (newProducts && newProducts.length) {
        const productsViewed = this.pageConfig.productsViewed + newProducts.length;

        if (isPopState) {
          // Replace the content during popstate
          this.productGrid.replaceChildren(...newProducts);
        } else if (direction == 'next') {
          this.productGrid.append(...newProducts);
        } else {
          this.productGrid.prepend(...newProducts);
        }

        // Update the attribute (triggers attributeChangedCallback lifecycle method)
        this.setAttribute(
          'data-page-config',
          JSON.stringify({
            ...this.pageConfig,
            currentPage: pageToFetch,
            productsViewed,
          })
        );

        if (pageToFetch == this.pageConfig.totalPages) {
          this.setButtonDisabled(this.loadMoreButtonNext);

          // Re-enable the previous button if we got here from popstate and there are more products to load
          if (this.pageConfig.productsViewed !== this.pageConfig.productsCount) {
            this.setButtonEnabled(this.loadMoreButtonPrevious);
          }
        } else if (pageToFetch == 1) {
          this.setButtonDisabled(this.loadMoreButtonPrevious);

          // Re-enable the next button if we got here from popstate and there are more products to load
          if (this.pageConfig.productsViewed !== this.pageConfig.productsCount) {
            this.setButtonEnabled(this.loadMoreButtonNext);
          }
        } else if (pageToFetch > 2) {
          this.loadButtons.map(this.setButtonEnabled);
        }

        this.setPaginationMeter();
        this.setProductCountInfo(html);
      }

      // Don't push new state if we're in a popstate
      if (!isPopState) {
        history.pushState(state, null, newUrl);
      }

      this.isLoading(false);
    } catch (error) {
      console.error(error);
      this.isLoading(false);
    }
  }

  setProductCountInfo(html) {
    if (!this.productTotalsInfo) return;

    const newProductTotals = html.querySelector(PRODUCT_TOTALS_INFO_SELECTOR);

    if (!newProductTotals) return;

    const firstViewedProduct = this.productTotalsInfo.querySelector('[data-first-viewed]');

    if (!firstViewedProduct) return;

    const firstViewedProductValue = parseInt(firstViewedProduct.textContent, 10);
    const newFirstViewedProductValue = parseInt(
      newProductTotals.querySelector('[data-first-viewed]').textContent,
      10
    );
    const lastViewedProduct = this.productTotalsInfo.querySelector('[data-last-viewed]');
    const lastViewedProductValue = parseInt(lastViewedProduct.textContent, 10);
    const newLastViewedProductValue = parseInt(
      newProductTotals.querySelector('[data-last-viewed]').textContent,
      10
    );

    if (!this.maxProductsViewed) {
      this.maxProductsViewed = lastViewedProductValue;
    }

    if (newLastViewedProductValue > this.maxProductsViewed) {
      this.maxProductsViewed = newLastViewedProductValue;
    }

    firstViewedProduct.textContent =
      newFirstViewedProductValue < firstViewedProductValue
        ? newFirstViewedProductValue
        : firstViewedProductValue;

    lastViewedProduct.textContent = this.maxProductsViewed;
  }

  isLoading(state = true) {
    this.loadButtons.forEach((button) => {
      button.disabled = state;
      state ? button.classList.add('loading') : button.classList.remove('loading');
    });
  }

  handlePopState = (event) => {
    const { state } = event;

    if (state?.collectionHandle === this.pageConfig.collectionHandle && state?.page !== undefined) {
      const previousPage = parseInt(this.previousUrl.searchParams.get('page'));
      const direction = state.page > previousPage ? 'next' : 'previous';

      this.handleLoadMoreProducts(direction, previousPage);
    }
  };

  connectedCallback() {
    if (this.isConnected) {
      const pageConfig = this.setPageConfig();

      if (pageConfig) {
        this.previousPageIndex = pageConfig.currentPage;
        this.nextPageIndex = pageConfig.currentPage;
        this.productGrid = document.querySelector(PRODUCT_GRID_SELECTOR);
        this.loadButtons = [...this.querySelectorAll(LOAD_MORE_BUTTONS_SELECTOR)];
        this.productTotalsInfo = document.querySelector(PRODUCT_TOTALS_INFO_SELECTOR);

        this.paginationMeter = this.querySelector(PAGINATION_METER_SELECTOR);
        this.paginationMeterText = this.querySelector(PAGINATION_METER_TEXT_SELECTOR);
        this.paginationMeterValue = this.querySelector(PAGINATION_METER_VALUE_SELECTOR);
        this.pagination = document.querySelector(`${WRAPPER_SELECTOR} ${PAGINATION_SELECTOR}`);
        this.previousUrl = new URL(window.location.href);

        // Check if we have a previous history object state
        const previousHistoryState = history.state?.constructor === Object ? history.state : {};

        // This ensures the user can click all the way back to initial page load
        history.replaceState(
          {
            ...previousHistoryState,
            page: pageConfig.currentPage,
            collectionHandle: pageConfig.collectionHandle,
          },
          null,
          window.location.href
        );

        window.addEventListener('popstate', this.handlePopState);

        this.loadButtons.forEach((button) => {
          const direction = button.dataset.collectionLoadMore;

          if (direction == 'previous') {
            this.loadMoreButtonPrevious = button;
          } else if (direction == 'next') {
            this.loadMoreButtonNext = button;
          }

          button.addEventListener('click', this.handleLoadMoreClick);
        });

        this.setPaginationMeter();
      }
    }
  }

  attributeChangedCallback() {
    this.setPageConfig();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handlePopState);

    this.loadButtons.forEach((button) => {
      button.removeEventListener('click', this.handleLoadMoreClick);
    });
  }
}

if (!customElements.get(ELM_NAME)) {
  window.customElements.define(ELM_NAME, DynamicProducts);
}
